import React from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import "./ServicePages.css";
import hero from "./assets/WebsiteSpeed/website-speed-optimization-services-hero.png";
import value from "./assets/WebsiteSpeed/what-affects-website-speed-breakdown.png";
import firewall from "./assets/WebsiteSpeed/firewalls-website-performance-balance.png";
import gauge from "./assets/WebsiteSpeed/business-website-management-speed-gauge.png";
import fiveStep from "./assets/WebsiteSpeed/website-speed-optimization-five-step-checklist.png";
import phone from "./assets/WebsiteSpeed/attoli-free-speed-check-call.png";
import Footer from "./Footer";

const WebsiteSpeed = () => {
  return (
    <div className="page-wrapper">
      <Helmet>
        <meta
          name="description"
          content="Attoli offers website speed optimization services built for accounting firms. Book a free call to see what is slowing your site down today."
        />
        <title>
          Website Speed Optimization Services For Accountants | Attoli
        </title>
        <link
          rel="canonical"
          href="https://attoli.com/services/website-speed-optimization-services-for-accountants"
        />
      </Helmet>

      <div className="servicePage-container">
        <section className="servicePage-section hero-section">
          <div className="servicePage-content">
            <div className="servicePage-left">
              <h1 className="servicePage-title">
                Website Speed Optimization Services For{" "}
                <span className="hero-accent">Accountants</span>
              </h1>
              <p className="servicePage-description">
                Our website speed optimization services help accounting firms
                load fast on every device. A slow or unresponsive site quietly
                pushes visitors away before they ever read your services.
              </p>

              <ul className="trust-badges">
                <li>No lock in contracts</li>
                <li>Built only for accounting and bookkeeping firms</li>
                <li>Free 20 minute call before you commit to anything</li>
              </ul>

              <div className="cta-block">
                <Link to="/contact" className="cta-button">
                  Book A Free Speed Check
                </Link>
              </div>
            </div>

            <div className="servicePage-right">
              <img
                src={hero}
                alt="Website loading bar filling up quickly next to a stopwatch icon representing fast page load speed"
                className="servicePage-image"
              />
            </div>
          </div>
        </section>

        <section className="servicePage-section pain-section">
          <div className="section-inner">
            <h2 className="section-title">
              Why A Slow Website Is Costing You Clients Right Now
            </h2>
            <p className="body-copy">
              Every extra second your site takes to load pushes visitors away.
              Studies show most people leave a page that takes more than three
              seconds to load. That visitor never sees your services or your
              contact form.
            </p>
            <p className="body-copy">
              Speed can also affect your rankings. Google factors load time into
              where your site shows up in search results. A really slow site
              quietly loses ground to competitors even when the content is just
              as good.
            </p>
            <p className="body-copy">
              Most accounting firms never test their site speed. It sits slow
              and nobody notices until visitors stop calling.
            </p>
            <p className="body-copy body-copy-emphasis">
              A fast site is not a nice to have. It is the difference between a
              visitor who stays and one who leaves.
            </p>
          </div>
        </section>

        <section className="servicePage-section value-section">
          <div className="section-inner">
            <h2 className="section-title">
              What Affects Website Speed On Your Site
            </h2>
            <p className="body-copy">
              Attoli reviews and fixes the pieces most accounting firms never
              think to check. Here is what that looks like in practice.
            </p>

            <div className="value-grid">
              <div className="value-card">
                <h3>Image Optimization</h3>
                <p>
                  We compress and resize images so they load fast without losing
                  quality. Large unoptimized images are one of the biggest
                  causes of a slow site.
                </p>
              </div>
              <div className="value-card">
                <h3>Script And Plugin Cleanup</h3>
                <p>
                  We remove unused scripts and plugins that quietly slow your
                  site down. Fewer unnecessary files means faster load times.
                </p>
              </div>
              <div className="value-card">
                <h3>Hosting Performance Review</h3>
                <p>
                  We check whether your hosting can actually support the speed
                  your site needs. The wrong hosting plan holds back every other
                  fix.
                </p>
              </div>
              <div className="value-card">
                <h3>Firewall And Security Balance</h3>
                <p>
                  We review your security setup to make sure firewalls protect
                  your site without slowing it down.
                </p>
              </div>
              <div className="value-card">
                <h3>Ongoing Speed Testing</h3>
                <p>
                  We test your site regularly instead of fixing it once and
                  walking away. Speed drifts over time without consistent
                  checks.
                </p>
              </div>
            </div>
            <img
              src={value}
              alt="Webpage broken into labeled pieces such as images scripts and hosting showing what adds up to total load time"
              className="inline-image"
            />
          </div>
        </section>

        <section className="servicePage-section keyword-section">
          <div className="section-inner">
            <h2 className="section-title">
              How Do Firewalls Affect Website Performance
            </h2>
            <p className="body-copy">
              Security and speed can pull against each other. A firewall that
              blocks bad traffic can also slow down real visitors if it is not
              configured correctly.
            </p>

            <img
              src={firewall}
              alt="Shield icon with a small clock representing the balance between security protection and site speed"
              className="inline-image"
            />

            <p className="body-copy">
              We tune your firewall settings so your site stays protected
              without adding delay. Security and speed are not opposites. They
              just need to be balanced correctly.
            </p>
          </div>
        </section>

        <section className="servicePage-section keyword-section-v2">
          <div className="section-inner">
            <h2 className="section-title">
              Why Business Website Management Means Ongoing Monitoring
            </h2>
            <p className="body-copy">
              A one time speed fix does not last. New plugins get added. Images
              get uploaded without resizing. Traffic patterns change. Without
              ongoing monitoring speed quietly drops again over time.
            </p>

            <img
              src={gauge}
              alt="Speed gauge needle staying steady in the green zone over time representing ongoing monitoring rather than a one time fix"
              className="inline-image"
            />

            <p className="body-copy">
              We treat speed the same way we treat uptime and security. Checked
              regularly instead of fixed once and forgotten.
            </p>
          </div>
        </section>

        <section className="servicePage-section process-section">
          <div className="section-inner">
            <h2 className="section-title">
              How Our Website Speed Optimization Process Works Step By Step
            </h2>
            <img
              src={fiveStep}
              alt="Five step checklist for website speed optimization covering images cleanup hosting firewalls and testing"
              className="inline-image"
            />
          </div>
        </section>

        <section className="servicePage-section faq-section">
          <div className="section-inner">
            <h2 className="section-title">Before You Book Your Call</h2>

            <details className="faq-item">
              <summary>How much faster will my site actually get?</summary>
              <p>
                Most sites see a noticeable improvement within the first round
                of fixes. Exact results depend on your starting point and
                hosting.
              </p>
            </details>

            <details className="faq-item">
              <summary>Do I have to sign a long contract?</summary>
              <p>
                No. We work month to month. You stay because the results are
                working not because a contract locks you in.
              </p>
            </details>

            <details className="faq-item">
              <summary>
                Will a firewall or security plugin slow my site down?
              </summary>
              <p>
                It can if it is not configured correctly. We review and tune
                your security setup so it protects your site without adding
                delay.
              </p>
            </details>

            <details className="faq-item">
              <summary>Do I need to understand any of this myself?</summary>
              <p>
                No. We handle the testing the fixes and the ongoing monitoring.
                You get a simple report showing what changed.
              </p>
            </details>
          </div>
        </section>

        <section className="servicePage-section closer-section">
          <div className="section-inner closer-inner">
            <img
              src={phone}
              alt="Calendar with a call icon and the text free 20 minute call"
              className="inline-image closer-image"
            />

            <h2 className="section-title">
              Ready To Find Out What Is Slowing Your Site Down?
            </h2>
            <p className="body-copy">
              Book a free 20 minute call. We will test your current site speed
              and show you exactly what is holding it back.
            </p>
            <Link to="/contact" className="cta-button cta-button-large">
              Book A Free Call Today
            </Link>
          </div>
        </section>
      </div>

      <Footer />
    </div>
  );
};

export default WebsiteSpeed;
